import { Controller } from "@hotwired/stimulus"

// Keeps a "Baustelle" (site) select in sync with a "Kunde" (customer) select.
// Selecting a customer narrows the site list to that customer's sites; selecting
// a site that belongs to a customer auto-fills the matching customer.
//
// Also orchestrates the quick-create dialogs: the selects' "neu anlegen" rows
// (select:addable) and the "+" buttons open the dialogs, and handleCreated
// injects the freshly created record as an option and selects it.
export default class extends Controller {
  static targets = ["customer", "site"]
  static values = {
    customerDialogId: { type: String, default: "quick-create-customer-dialog" },
    siteDialogId: { type: String, default: "quick-create-site-dialog" },
  }

  connect() {
    this.filterSites()
  }

  // Open the customer quick-create dialog, prefilled with the typed search term
  // when triggered from the select's addable row (empty from the "+" button).
  openCustomerQuickCreate(event) {
    this.openQuickCreate(this.customerDialogIdValue, event)
  }

  openSiteQuickCreate(event) {
    this.openQuickCreate(this.siteDialogIdValue, event)
  }

  openQuickCreate(dialogId, event) {
    window.dispatchEvent(
      new CustomEvent("quick-create:open", {
        detail: {
          dialogId,
          term: event.detail?.term || "",
          customerId: this.customerTarget.value,
        },
      }),
    )
  }

  // A quick-create dialog saved a record: inject it as an option, select it, and
  // keep the customer<->site coupling intact. New site options carry
  // data-customer-id so filterSites/syncCustomer treat them like any other.
  handleCreated({ detail: { kind, id, name, customerId } }) {
    if (kind === "customer") {
      this.customerTarget.append(new Option(name, id))
      this.refreshSelect(this.customerTarget)
      // The change dispatched here re-runs filterSites; an already-selected site
      // stays visible via the "selected is never hidden" rule.
      this.setValue(this.customerTarget, id)
    } else if (kind === "site") {
      const option = new Option(name, id)
      option.dataset.customerId = customerId || ""
      this.siteTarget.append(option)
      this.refreshSelect(this.siteTarget)
      // The change dispatched here runs syncCustomer, which back-fills the
      // site's customer and re-filters.
      this.setValue(this.siteTarget, id)
    }
  }

  // SlimSelect snapshots its data; after mutating the underlying options, ask the
  // wrapping select controller to rebuild (same pattern as billing_offer_tree).
  refreshSelect(target) {
    this.application.getControllerForElementAndIdentifier(target, "select")?.refresh()
  }

  // Show only the sites belonging to the selected customer. With no customer
  // selected, all sites are shown. The currently-selected option is never
  // hidden so legacy/inconsistent records keep their value.
  filterSites() {
    const customerId = this.customerTarget.value
    const selectedValue = this.siteTarget.value
    let selectedHidden = false

    Array.from(this.siteTarget.options).forEach((option) => {
      if (option.value === "") {
        return
      }

      const optionCustomerId = option.dataset.customerId || ""
      const matches = customerId === "" || optionCustomerId === customerId
      const isSelected = option.value === selectedValue

      const visible = matches || isSelected
      // SlimSelect reads an option as hidden when it has `hidden` OR
      // `style.display = "none"`, and rebuilds the native options with only
      // `hidden` — so `hidden` is the one to drive; resetting `style.display`
      // alone never shows a rebuilt option again. `disabled` covers the no-JS
      // fallback.
      option.hidden = !visible
      option.disabled = !visible

      if (!visible && isSelected) {
        selectedHidden = true
      }
    })

    if (selectedHidden) {
      this.setValue(this.siteTarget, "")
    }
  }

  // When a site with a customer is picked, set the customer select to match.
  syncCustomer() {
    const selectedOption = this.siteTarget.options[this.siteTarget.selectedIndex]
    const optionCustomerId = selectedOption && selectedOption.dataset.customerId

    if (optionCustomerId && this.hasCustomerOption(optionCustomerId)) {
      this.setValue(this.customerTarget, optionCustomerId)
    }

    this.filterSites()
  }

  hasCustomerOption(value) {
    return Array.from(this.customerTarget.options).some((option) => option.value === value)
  }

  // Assign a select's value programmatically. Goes through the SlimSelect
  // wrapper's setSelected when present (a bare value + change can be reverted by
  // SlimSelect on its first programmatic change after init); the wrapper still
  // dispatches a native change, so bound actions fire. Settles after one pass
  // since a blank/duplicate value produces no further change.
  setValue(target, value) {
    const selectController = this.application.getControllerForElementAndIdentifier(target, "select")
    if (selectController) {
      selectController.setSelected(value)
    } else {
      target.value = value
      target.dispatchEvent(new Event("change"))
    }
  }
}
